export class WireError extends Error {
  readonly status: number;
  readonly url: string;
  readonly wireVersion: string | undefined;

  constructor(message: string, status: number, url: string, wireVersion: string | undefined) {
    super(message);
    this.name = 'WireError';
    this.status = status;
    this.url = url;
    this.wireVersion = wireVersion;
  }
}

export class Forbidden extends WireError {
  constructor(url: string, wireVersion: string | undefined) {
    super('Forbidden', 403, url, wireVersion);
    this.name = 'Forbidden';
  }
}

export class NotExist extends WireError {
  constructor(url: string, wireVersion: string | undefined) {
    super('Not found', 404, url, wireVersion);
    this.name = 'NotExist';
  }
}

export class Conflict extends WireError {
  constructor(url: string, wireVersion: string | undefined) {
    super('OCC conflict (409)', 409, url, wireVersion);
    this.name = 'Conflict';
  }
}

/**
 * The OTHER 409 (gateway >= 0.1.2, body `{"error":"idempotency_conflict"}`): your batch's
 * client_seq range was (fully or partially) consumed by a different request — a sibling tab
 * sharing the keypair, an event stored without an event_id, a straddling batch, or trimmed
 * history. The write did NOT land and retrying the same body can never succeed. Re-derive
 * client_seq — from `lastClientSeq` when present (the stream alone under-derives once history
 * is trimmed) — and do not rebase a version guard. Only reachable on event_id-stamped writes.
 */
export class IdempotencyConflict extends WireError {
  readonly lastClientSeq: number | undefined;

  constructor(url: string, wireVersion: string | undefined, lastClientSeq: number | undefined) {
    super('idempotency conflict (409): client_seq consumed by another request', 409, url, wireVersion);
    this.name = 'IdempotencyConflict';
    this.lastClientSeq = lastClientSeq;
  }
}

/**
 * The subscription's EventSource permanently failed (readyState CLOSED — per spec a
 * non-200 handshake response kills it with NO auto-reconnect: gateway SSE-cap 503, 401 on
 * aged credentials at reconnect, 403 on ACL revocation). The generator throws this so the
 * layer above can rebuild the subscription with fresh credentials (W4/O10).
 */
export class SubscribeClosed extends WireError {
  constructor(url: string) {
    super('subscription EventSource permanently closed', 0, url, undefined);
    this.name = 'SubscribeClosed';
  }
}

export class Unauthorized extends WireError {
  constructor(url: string, wireVersion: string | undefined) {
    super('Unauthorized', 401, url, wireVersion);
    this.name = 'Unauthorized';
  }
}

/**
 * The OTHER 401: the gateway saw this exact `(client_id, nonce)` inside its freshness window
 * (body `{"error":"nonce_replay"}`). Nothing is wrong with the credentials.
 *
 * One keypair legitimately backs many `Gateway` instances. Browser identity lives in IndexedDB,
 * which is per-origin and not per-tab, so two tabs of one app are two Gateways with one
 * `client_id`. Each carries its own monotonic nonce source (a closure over `last`, seeded from
 * `Date.now()`), and two of them reading in the same millisecond emit the same nonce. The gateway
 * records the first and refuses the second.
 *
 * Retryable, and `signedRequestWithRetry` already retries it, so this rarely escapes. When it
 * does, the re-sign allowance was exhausted: many contexts are sharing one keypair and colliding
 * faster than re-signing clears them. Retrying the whole request is safe and correct.
 *
 * Extends `Unauthorized` deliberately, and that is load-bearing rather than taxonomy. Callers
 * already branch on `instanceof Unauthorized` (`onlineEngine.ts` treats it as a non-retryable
 * access fact and rethrows), so a sibling class would silently reroute an escaped replay into a
 * different path. Subtyping keeps every existing consumer byte-identical and lets code that cares
 * about the difference test for `NonceReplay` specifically.
 */
export class NonceReplay extends Unauthorized {
  constructor(url: string, wireVersion: string | undefined) {
    super(url, wireVersion);
    this.name = 'NonceReplay';
    this.message = 'nonce replay: this (client_id, nonce) was already used inside the freshness window';
  }
}

// The machine code the gateway stamps on a replay refusal (WIRE-CONTRACT §envelope, gateway
// >= 0.1.3, body `{"error":"nonce_replay"}`). Same shape as the idempotency 409 and for the same
// reason: the status alone cannot separate a replay from a revoked key.
//
// Matched as a CODE, never as the message beside it. That message is for humans reading a log; a
// client keyed to the prose breaks silently the day the prose is reworded, and the symptom is an
// app yo-yoing offline whenever a second tab writes, with every unit test still green.
//
// Anything else is a GENUINE 401 and must stay `Unauthorized`: a revoked key that retried on every
// request would spin instead of failing, which is worse than the fault this fixes.
const NONCE_REPLAY_CODE = 'nonce_replay';

export function isNonceReplayBody(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as Record<string, unknown>)['error'] === NONCE_REPLAY_CODE
  );
}

export class Unavailable extends WireError {
  readonly retryAfterMs: number | undefined;

  constructor(status: number, url: string, wireVersion: string | undefined, retryAfterMs: number | undefined) {
    super(`Service unavailable (HTTP ${status})`, status, url, wireVersion);
    this.name = 'Unavailable';
    this.retryAfterMs = retryAfterMs;
  }
}

export class TooLarge extends WireError {
  constructor(url: string, wireVersion: string | undefined) {
    super('Event too large to send, even as a single item', 413, url, wireVersion);
    this.name = 'TooLarge';
  }
}

export class WireVersionMismatch extends WireError {
  readonly expected: string;
  readonly actual: string;

  constructor(url: string, expected: string, actual: string) {
    super(`Wire-version mismatch: expected ${expected}, got ${actual}`, 400, url, actual);
    this.name = 'WireVersionMismatch';
    this.expected = expected;
    this.actual = actual;
  }
}

// Turn any non-2xx into a typed throw BEFORE the caller parses the body. Without this a
// 401/429/500/503 body parses as a corrupt success object and silent data loss follows.
// Callers that special-case a status (write's 413 split-and-retry) handle it before calling here.
// Async because two statuses are distinguished by BODY shape (WIRE-CONTRACT §errors): the
// two 409s ({"error":"idempotency_conflict"} vs the OCC {"message": ...}) and the two 401s
// (a nonce replay vs a genuine credential failure).
export async function throwForStatus(response: Response, url: string): Promise<void> {
  if (response.status >= 200 && response.status < 300) return;
  const wireVersion = response.headers.get('X-Wire-Version') ?? undefined;
  switch (response.status) {
    case 401: {
      // Same shape as the 409 split below: the status alone does not identify the fault.
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        throw new Unauthorized(url, wireVersion);
      }
      if (isNonceReplayBody(body)) throw new NonceReplay(url, wireVersion);
      throw new Unauthorized(url, wireVersion);
    }
    case 403:
      throw new Forbidden(url, wireVersion);
    case 404:
      throw new NotExist(url, wireVersion);
    case 409: {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        throw new Conflict(url, wireVersion);
      }
      if (typeof body === 'object' && body !== null && (body as Record<string, unknown>)['error'] === 'idempotency_conflict') {
        const last = (body as Record<string, unknown>)['last_client_seq'];
        throw new IdempotencyConflict(url, wireVersion, typeof last === 'number' ? last : undefined);
      }
      throw new Conflict(url, wireVersion);
    }
    case 429:
    case 503: {
      const retryAfter = response.headers.get('Retry-After');
      const seconds = retryAfter === null ? NaN : Number(retryAfter);
      throw new Unavailable(response.status, url, wireVersion, Number.isFinite(seconds) ? seconds * 1000 : undefined);
    }
    default:
      throw new WireError(`Unexpected response status ${response.status}`, response.status, url, wireVersion);
  }
}
